import { StaticImageData } from 'next/image';
import { useEffect, useState } from 'react';

// Import blog images
import blogImage1 from '../assets/blog/appointment-blog.jpg';
import blogImage2 from '../assets/blog/custom-ai-blog.jpg';
import blogImage3 from '../assets/blog/ai-web-applications.jpg'; // You'll need to add this image

// Import the image for the supply chain blog post
import supplyChainImage from '../assets/blog/supply-chain-resilience.jpg';
import aiAgentImage from '../assets/blog/ai-agents-blog.jpg'; // Assuming this is your added image
import aiDashboardImage from '../assets/blog/ai-dashboard.jpg';
import AIAgentsWorkflowBlog from '../content/BlogPosts/AiAgentDevelopment';
import AICustomDashboardBlog from '../content/BlogPosts/aiDashboards';

export interface Author {
  name: string;
  title?: string;
  avatar?: {
    src: string | StaticImageData;
    alt?: string;
  };
  bio?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  contentComponent?: string; // Reference to component name
  contentPath?: string; // Path to content file instead of inline content
  date: string; // YYYY-MM-DD format
  author: Author;
  categories: string[];
  tags: string[];
  coverImage: {
    src: string | StaticImageData;
    alt?: string;
  };
  readTime: number; // in minutes
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
}

export const blogPosts: BlogPost[] = [
  {
    slug: "custom-ai-dashboards-competitive-advantage",
    title: "Why Custom AI Dashboards Are Your Next Competitive Advantage",
    excerpt: "Ever felt swamped by a sea of spreadsheets? Bespoke AI dashboards deliver the exact insights you need, exactly when you need them—without the guesswork.",
    contentComponent: "AICustomDashboardBlog",
    date: "2025-05-05", // Today's date or whenever you want it published
    author: {
      name: "BetterByAI Team",
      title: "Data Intelligence Solutions",
      bio: "The BetterByAI team specializes in creating custom AI dashboards that turn complex data into actionable insights."
    },
    categories: ["AI Dashboards", "Business Intelligence"],
    tags: ["ai dashboards", "business intelligence", "data analytics", "custom solutions", "decision making"],
    coverImage: {
      src: aiDashboardImage,
      alt: "Custom AI dashboard showing business analytics"
    },
    readTime: 4,
    seo: {
      metaTitle: "Why Custom AI Dashboards Are Your Next Competitive Advantage | BetterByAI",
      metaDescription: "Discover how bespoke AI dashboards deliver real-time insights, predictive forecasts, and democratized data access to give your business a competitive edge.",
      keywords: ["ai dashboards", "business intelligence", "data analytics", "competitive advantage", "real-time insights"]
    }
  },
  {
    slug: "building-resilient-supply-chains",
    title: "Building Resilient Supply Chains for a Disruptive Future",
    excerpt: "Explore key insights from PwC's research on building resilient supply chains and learn practical strategies for enhancing operational continuity in today's volatile business environment.",
    contentComponent: "BuildingResilientSupplyChains",
    date: "2025-04-04",
    author: {
      name: "BetterByAI Team",
      title: "Supply Chain Solutions",
      bio: "Our team specialises in developing AI-powered solutions for modern supply chain challenges."
    },
    categories: ["Supply Chain", "Business Resilience", "Risk Management"],
    tags: ["supply chain", "resilience", "risk assessment", "digital transformation", "PwC research"],
    coverImage: {
      src: supplyChainImage,
      alt: "Digital visualisation of a global supply chain network"
    },
    readTime: 6,
    seo: {
      metaTitle: "Building Resilient Supply Chains for a Disruptive Future | BetterByAI",
      metaDescription: "Learn how to build resilient supply chains with insights from PwC research and practical strategies for managing disruption in today's volatile business environment.",
      keywords: ["supply chain resilience", "risk management", "digital supply chain", "business continuity", "PwC research"]
    }
  },
  {
    slug: "ai-web-applications-business-transformation",
    title: "7 Ways Companies Are Using AI Web Applications to Transform Their Operations (That You Haven't Considered)",
    excerpt: "While everyone talks about ChatGPT and generative AI, forward-thinking businesses are quietly implementing AI-powered web applications that deliver tangible results through intuitive interfaces.",
    contentComponent: "AIWebApplicationsBlog", // Reference to the component we created
    date: "2025-03-29", // Today's date
    author: {
      name: "BetterByAI",
      title: "Development Team",
    },
    categories: ["Web Applications", "AI Implementation"],
    tags: ["SaaS", "Business Portals", "Web Development", "AI Integration", "User Interfaces", "Enterprise Applications"],
    coverImage: {
      src: blogImage3, // Using the imported image
      alt: "AI web applications transforming business operations"
    },
    readTime: 7,
    seo: {
      metaTitle: "7 Ways Companies Are Using AI Web Applications in 2025 | BetterByAI",
      metaDescription: "Discover how businesses are implementing AI through intuitive web interfaces - from predictive maintenance to knowledge management systems that deliver real business value.",
      keywords: ["AI web applications", "business portals", "web interfaces", "AI implementation", "enterprise applications", "predictive maintenance", "document processing", "resource allocation"]
    }
  },
  {
    slug: "custom-ai-solutions-roi",
    title: "Is Custom AI Worth the Investment? 89% of Businesses Waste Money on Generic Solutions",
    excerpt: "Discover when bespoke AI development delivers superior business value and how to evaluate the true return on investment.",
    contentComponent: "CustomAISolutionsROI", // Reference to the component we created
    date: "2025-03-28", // Updated to 2025
    author: {
      name: "BetterByAI",
      title: "AI Strategy Team",
    },
    categories: ["Custom AI", "Business Strategy"],
    tags: ["AI ROI", "Custom Development", "Enterprise AI", "Digital Transformation"],
    coverImage: {
      src: blogImage2, // Using the imported image
      alt: "Custom AI solutions vs off-the-shelf comparison"
    },
    readTime: 7,
    seo: {
      metaTitle: "The ROI of Custom AI Solutions vs Off-the-Shelf Tools | BetterByAI",
      metaDescription: "Learn when to invest in custom AI development versus using generic solutions, with ROI analysis and implementation strategies for business leaders.",
      keywords: ["custom AI development", "AI ROI", "enterprise AI strategy", "bespoke machine learning", "AI business value"]
    }
  },
  {
    slug: "appointment-ai-agents",
    title: "The True Cost of Poor Appointment Management",
    excerpt: "Did you know that the average UK small business loses over £1,200 monthly due to inefficient scheduling?",
    contentComponent: "GettingStartedWithAIAgents", // Reference to component name
    date: "2025-03-27", // Updated to 2025
    author: {
      name: "BetterByAI",
      title: "Author",
    },
    categories: ["AI Agents", "Implementation"],
    tags: ["AI", "Automation", "Business", "Getting Started"],
    coverImage: {
      src: blogImage1, // Using the imported image
      alt: "Why UK Businesses Are Losing £1,200+ Monthly"
    },
    readTime: 8,
    seo: {
      metaTitle: "The True Cost of Poor Appointment Management | BetterByAI",
      metaDescription: "Discover how AI appointment scheduling can save UK businesses over £1,200 monthly and improve customer satisfaction through automated booking systems.",
      keywords: ["appointment management", "AI scheduling", "business automation", "meeting booking system", "appointment AI", "scheduling efficiency", "cost of missed appointments"]
    }
  },
  {
    slug: "ai-agents-transforming-business-workflows",
    title: "The Quiet Revolution: How Custom AI Agents Are Transforming Business Workflows in 2025",
    excerpt: "Discover how businesses are deploying custom AI agents to revolutionize everything from lead qualification to document processing and supply chain management.",
    contentComponent: "AIAgentsWorkflowBlog",
    date: "2025-05-01", // Adjust date as needed
    author: {
      name: "BetterByAI Team",
      title: "AI Solutions Architects",
      bio: "The BetterByAI team has helped dozens of companies deploy custom AI solutions that integrate seamlessly with existing workflows."
    },
    categories: ["AI Agents", "Workflow Automation"],
    tags: ["ai agents", "custom solutions", "automation", "business workflows", "productivity"],
    coverImage: {
      src: aiAgentImage,
      alt: "AI agent automating business workflows"
    },
    readTime: 5,
    seo: {
      metaTitle: "How Custom AI Agents Transform Business Workflows in 2025 | BetterByAI",
      metaDescription: "Learn how businesses are using custom AI agents to automate lead qualification, customer support, supply chain management, DevOps, and document processing.",
      keywords: ["ai agents", "workflow automation", "business productivity", "custom ai solutions"]
    }
  },
  // Add more blog posts here
];

export default function BlogDetail() {
  const [content, setContent] = useState('');
  // Other state and logic...
  
  useEffect(() => {
    if (post) {
      // Dynamically load the content file
      fetch(post.contentPath)
        .then(response => response.text())
        .then(text => {
          // If using markdown, convert to HTML here
          setContent(text);
        });
    }
  }, [post]);
  
  // Then use content in your render instead of post.content
}

// At the bottom of the file, make sure to import all your blog components first
import AIAgentsWorkflowBlog from '../content/BlogPosts/AiAgentDevelopment';
import BuildingResilientSupplyChains from '../content/BlogPosts/BuildingResilientSupplyChains';
import AIWebApplicationsBlog from '../content/BlogPosts/AIWebApplicationsBlog';
import CustomAISolutionsROI from '../content/BlogPosts/CustomAISolutionsROI';
import GettingStartedWithAIAgents from '../content/BlogPosts/GettingStartedWithAIAgents';
import AICustomDashboardBlog from '../content/BlogPosts/aiDashboards';

// Then update the importedBlogComponents object
export const importedBlogComponents = {
  AICustomDashboardBlog,
  AIAgentsWorkflowBlog,
  BuildingResilientSupplyChains,
  AIWebApplicationsBlog,
  CustomAISolutionsROI,
  GettingStartedWithAIAgents
};